import { Link, useNavigate } from "react-router-dom";
import { Instagram, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LocationSearch } from "@/components/LocationSearch";
import { trackHomeCtaClick, trackAuthStart } from "@/lib/analytics";

interface LandingNavProps {
  /** Slug of the location prefilled in the search control. */
  locationSlug: string;
  /** Called in addition to navigation (keeps the auth block in sync on-page). */
  onCreateAccount: () => void;
  onSignIn: () => void;
}

/**
 * Landing-only top bar. The only bright-red element on the page besides the
 * signup block is the "Create account" button.
 */
export const LandingNav = ({ locationSlug, onCreateAccount, onSignIn }: LandingNavProps) => {
  const navigate = useNavigate();

  const goToLocation = (slug: string) => {
    trackHomeCtaClick("nav_location_search", `/leaderboard/${slug}`);
    navigate(slug === "world" ? "/leaderboard" : `/leaderboard/${slug}`);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border/50 bg-background/90 backdrop-blur-lg">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-8">
        <div className="flex items-center justify-between gap-3 py-2 md:py-3">
          <Link to="/" aria-label="BRRGR home" className="select-none shrink-0">
            <span className="text-xl font-extrabold uppercase tracking-wide text-primary leading-none">
              BRRGR
            </span>
          </Link>

          <div className="flex items-center gap-2 md:gap-4">
            <div className="hidden md:block">
              <LocationSearch
                value={locationSlug}
                onValueChange={goToLocation}
                showLabel
                showValue={false}
                className="h-9 w-[220px] text-sm"
              />
            </div>

            <Link
              to="/leaderboards"
              className="hidden md:inline text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => trackHomeCtaClick("nav_leaderboards", "/leaderboards")}
            >
              Leaderboards
            </Link>

            <Link
              to="/signin"
              onClick={() => {
                trackHomeCtaClick("nav_sign_in", "/signin");
                onSignIn();
              }}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Sign in
            </Link>

            <div className="hidden sm:flex items-center gap-2 text-muted-foreground">
              <a
                href="https://instagram.com/brrgr.app"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="BRRGR on Instagram"
                className="hover:text-foreground transition-colors"
                onClick={() => trackHomeCtaClick("nav_instagram", "https://instagram.com/brrgr.app")}
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href="https://linkedin.com/company/brrgr"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="BRRGR on LinkedIn"
                className="hover:text-foreground transition-colors"
                onClick={() => trackHomeCtaClick("nav_linkedin", "https://linkedin.com/company/brrgr")}
              >
                <Linkedin className="h-4 w-4" />
              </a>
            </div>

            <Button
              asChild
              size="sm"
              className="bg-primary hover:bg-primary-hover text-primary-foreground bg-none shadow-soft"
            >
              <Link
                to="/signup"
                onClick={() => {
                  trackHomeCtaClick("nav_create_account", "/signup");
                  trackAuthStart("nav_create_account");
                  onCreateAccount();
                }}
              >
                Create account
              </Link>
            </Button>
          </div>
        </div>

        {/* Mobile: location search on its own row so it stays usable */}
        <div className="md:hidden pb-2">
          <LocationSearch
            value={locationSlug}
            onValueChange={goToLocation}
            showLabel
            showValue={false}
            className="h-9 w-full text-sm"
          />
        </div>
      </div>
    </header>
  );
};

export default LandingNav;
